import { Box } from "@mui/material";
import type { MouseEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { GuideStepText } from "./GuidePlayer";
import { GuidePlayer } from "./GuidePlayer";
import { chapterNumber, type ManualChapterId } from "./manualChapters";
import { jumpToManualSection, scrollToManualTop } from "./manualJump";
import { ManualChapterList } from "./ManualChapterList";
import { MANUAL_STYLES } from "./manualStyles";
import { ManualText } from "./ManualText";
import {
  hasChapterIntro,
  type ManualChapter,
  type ManualTopic,
} from "./manualTopics";
import { ChevronIcon, ListIcon } from "./TocIcons";
import { UpArrowIcon } from "./UpArrowIcon";

export interface ManualChapterText {
  name: string;
  open?: string;
}

export interface ManualTopicText {
  /** 절 목록·검색 결과·절 제목에 같이 쓰는 이름 */
  title: string;
  note?: string;
  steps?: readonly GuideStepText[];
}

/** 페이지가 매뉴얼 안에 꽂는 자리 — 다른 도메인의 컴포넌트·규칙을 매뉴얼이 직접 import 하지 않게 한다 */
export interface ManualSlots {
  /** 「매핑 요청과 GCP」 절의 GCP 자동 인식 가이드 진입(GCP 행·요청 모달과 같은 가이드) */
  mappingGuideEntry?: ReactNode;
}

const jumpLink = (id: string) => (event: { preventDefault: () => void }) => {
  event.preventDefault();
  jumpToManualSection(id);
};

/** 「매핑 요청과 GCP」 절의 GCP 안내 — GCP 제출 경로·자동 인식·가이드 진입, 요청할 수 있는 사람·처리 시간 */
function MappingNotes({ mappingGuideEntry }: ManualSlots) {
  const { t } = useTranslation("landing");
  return (
    <Box sx={MANUAL_STYLES.note}>
      <p>
        <b>{t("site.manual.noteLabel.note")}</b>
        {t("site.manual.mapping.gcpPath")} {t("site.manual.mapping.autoDetect")}
      </p>
      {mappingGuideEntry ? (
        <Box sx={MANUAL_STYLES.guideEntry}>{mappingGuideEntry}</Box>
      ) : null}
      <p>
        {t("site.manual.mapping.ownerOnly")}{" "}
        {t("site.manual.mapping.processingTime")}
      </p>
    </Box>
  );
}

/** 절 하나(제목 · 여는 법 · 단계별 안내 플레이어 또는 참고) — 손님 맛보기(`ManualGuestPreview`)도 첫 절을 이것으로 그린다 */
export function ManualTopicBlock({
  topic,
  text,
  intro,
  slots,
}: {
  topic: ManualTopic;
  text: ManualTopicText;
  /** 그 장 화면을 여는 법 — 장 첫 절에만 */
  intro?: string;
  slots: ManualSlots;
}) {
  const { t } = useTranslation("landing");
  // 참고는 플레이어가 있으면 플레이어 안 자리(aside — 가로 터치 화면에서 오른쪽 칸)에, 없으면 글 바로 아래
  const note =
    topic.note && text.note ? (
      <Box component="p" sx={MANUAL_STYLES.note}>
        <b>{t(`site.manual.noteLabel.${topic.note}`)}</b>
        <ManualText text={text.note} />
      </Box>
    ) : null;
  return (
    <Box
      component="article"
      id={topic.id}
      tabIndex={-1}
      sx={MANUAL_STYLES.topic}
    >
      <Box component="h3" sx={MANUAL_STYLES.topicTitle}>
        {text.title}
      </Box>
      {intro ? (
        <Box component="p" sx={MANUAL_STYLES.topicIntro}>
          <ManualText text={intro} />
        </Box>
      ) : null}
      {topic.guide && text.steps ? (
        <GuidePlayer
          guideKey={topic.guide}
          title={text.title}
          steps={text.steps}
          aside={note}
        />
      ) : (
        note
      )}
      {topic.extra === "mapping" ? <MappingNotes {...slots} /> : null}
    </Box>
  );
}

export interface ManualChapterSectionProps {
  chapter: ManualChapter;
  index: number;
  chapters: Readonly<Record<ManualChapterId, ManualChapterText>>;
  topics: Readonly<Record<string, ManualTopicText>>;
  slots: ManualSlots;
  /** 「전체 목차」 펼침 — 장마다 왼쪽 열이 따로 있어도 사용자에게는 목차 하나라 페이지가 하나로 가진다 */
  allChaptersOpen: boolean;
  /** 누른 단추를 넘긴다 — Esc 로 닫을 때 페이지가 초점을 그 단추로 돌린다 */
  onAllChaptersToggle: (toggle: HTMLButtonElement) => void;
  /** 전체 목차에서 고른 장으로 옮긴다(바로 이동 + 접기) */
  onChapterJump: (id: string) => void;
}

/**
 * 장(service-manual 「매뉴얼 장 구성」): 왼쪽에 번호·장 이름·그 장의 절 목록, 맨 아래 한 줄에 「전체 목차」·「처음으로」
 * (넓은 화면에서 화면 높이로 붙어 있고 줄은 맨 아래 고정, 펼친 장 목록은 그 줄 위에 뜨는 카드), 오른쪽에 절(첫 절 제목 아래에 그 장 화면을 여는 법).
 * 카드 밖 누름 판정은 페이지가 `data-all-chapters` 로 한다.
 * 절 목록의 지금 절 표시는 페이지가 DOM 속성으로 단다(`data-toc-for`).
 */
export function ManualChapterSection({
  chapter,
  index,
  chapters,
  topics,
  slots,
  allChaptersOpen,
  onAllChaptersToggle,
  onChapterJump,
}: ManualChapterSectionProps) {
  const { t } = useTranslation("landing");
  const { name, open } = chapters[chapter.id];
  const headingId = `${chapter.id}-name`;
  const allChaptersId = `${chapter.id}-all-chapters`;
  return (
    <Box
      component="section"
      id={chapter.id}
      tabIndex={-1}
      aria-labelledby={headingId}
      sx={MANUAL_STYLES.chapter}
    >
      <Box component="aside" sx={MANUAL_STYLES.side}>
        <Box component="p" sx={MANUAL_STYLES.chapterNumber}>
          {chapterNumber(index)}
        </Box>
        <Box component="h2" id={headingId} sx={MANUAL_STYLES.chapterName}>
          <a href={`#${chapter.id}`} onClick={jumpLink(chapter.id)}>
            {name}
          </a>
        </Box>
        <Box
          component="ol"
          aria-label={t("site.manual.tocLabel", { chapter: name })}
          sx={MANUAL_STYLES.toc}
        >
          {chapter.topics.map(({ id }) => (
            <li key={id}>
              <a href={`#${id}`} data-toc-for={id} onClick={jumpLink(id)}>
                {topics[id].title}
              </a>
            </li>
          ))}
        </Box>
        <Box sx={MANUAL_STYLES.sideFoot} data-all-chapters="">
          <Box sx={MANUAL_STYLES.sideActions}>
            <Box
              component="button"
              type="button"
              aria-expanded={allChaptersOpen}
              aria-controls={allChaptersId}
              onClick={(event: MouseEvent<HTMLButtonElement>) =>
                onAllChaptersToggle(event.currentTarget)
              }
              sx={MANUAL_STYLES.allChaptersToggle}
            >
              <ListIcon />
              {t("site.manual.allChapters")}
              <ChevronIcon />
            </Box>
            <Box
              component="a"
              href="#main"
              onClick={(event) => {
                event.preventDefault();
                scrollToManualTop();
              }}
              sx={MANUAL_STYLES.backToStart}
            >
              <UpArrowIcon />
              {t("site.manual.backToStart")}
            </Box>
          </Box>
          <ManualChapterList
            id={allChaptersId}
            hidden={!allChaptersOpen}
            variant="side"
            chapters={chapters}
            currentChapter={chapter.id}
            onJump={onChapterJump}
          />
        </Box>
      </Box>
      <Box sx={MANUAL_STYLES.body}>
        {chapter.topics.map((topic, topicIndex) => (
          <ManualTopicBlock
            key={topic.id}
            topic={topic}
            text={topics[topic.id]}
            intro={hasChapterIntro(chapter, topicIndex) ? open : undefined}
            slots={slots}
          />
        ))}
      </Box>
    </Box>
  );
}
